// Verificacao do PDF da factura emitida ao cliente (Sage).
// Compara nome, NIF e 3 valores (base s/IVA, IVA, total c/IVA) com a encomenda
// antes de fazer upload + envio email -- evita anexar factura de outro cliente
// ou com valores trocados.
//
// Pipeline:
//   1) extractTextFromPdf (re-uso de parseFatura.js)
//   2) extrairCampos(text) -> { nif, nome_candidates, valores }
//   3) compararComEncomenda(extraido, encomenda) -> { ok, problemas, extraido, esperado }

import { extractTextFromPdf } from './parseFatura'

// Tolerancia para comparar valores monetarios (centimos de arredondamento)
const TOL_VALOR = 0.05

/** Normaliza numero PT/EN -> float */
function parseNumero(s) {
  if (s == null) return 0
  let t = String(s).replace(/€|EUR|\s/gi, '').trim()
  if (t.includes(',') && t.includes('.')) {
    if (t.lastIndexOf(',') < t.lastIndexOf('.')) t = t.replace(/,/g, '')
    else t = t.replace(/\./g, '').replace(',', '.')
  } else if (t.includes(',') && !t.includes('.')) {
    const partes = t.split(',')
    if (partes[1] && partes[1].length === 2) t = t.replace(',', '.')
    else t = t.replace(/,/g, '')
  }
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : 0
}

/** Remove acentos, lowercase, trim, colapsa espacos */
function norm(s) {
  return String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim()
}

/** Normaliza NIF: aceita "PT 123 456 789", "123.456.789", etc. */
function normNif(s) {
  if (!s) return ''
  return String(s).replace(/[^\d]/g, '').replace(/^351/, '')
}

/**
 * Extrai o ULTIMO numero monetario de uma string (formato PT ou US, 2 decimais),
 * IGNORANDO percentagens (numero seguido de %). Devolve {valor,idx} | null.
 */
function extrairUltimoValorMonetario(s) {
  if (!s) return null
  const re = /(\d{1,3}(?:[.\s]\d{3})*(?:[,.]\d{2}))(?!\s*%)/g
  let last = null
  let m
  while ((m = re.exec(s)) !== null) {
    const valor = parseNumero(m[1])
    if (valor > 0) last = { valor, idx: m.index }
  }
  return last
}

/**
 * Extrai o PRIMEIRO numero monetario a partir de uma posicao indicada.
 * Usado para "valor logo a seguir a keyword X" — robusto a linhas com
 * varias colunas (ex: "Base 1095,48 IVA 251,96" devolve 1095,48 quando
 * partimos de apos "Base").
 */
function extrairPrimeiroValorMonetarioApos(s, posInicio = 0) {
  if (!s) return null
  const re = /(\d{1,3}(?:[.\s]\d{3})*(?:[,.]\d{2}))(?!\s*%)/g
  re.lastIndex = posInicio
  const m = re.exec(s)
  if (!m) return null
  const valor = parseNumero(m[1])
  return valor > 0 ? { valor, idx: m.index } : null
}

/** Extrai NIF, valores, candidatos a nome, nº fatura e data do texto do PDF.
 *  @param {string} rawText
 *  @param {object} opts - { nifEsperado: string } — se passado, usa-se para localizar
 *    a area do cliente no PDF (mais robusto que assumir "ultimo NIF = cliente")
 */
export function extrairCampos(rawText, opts = {}) {
  const nifEsperadoNorm = normNif(opts.nifEsperado || '')
  const text = (rawText || '').replace(/\r\n/g, '\n').replace(/ /g, ' ')
  const linhas = text.split('\n').map(l => l.trim()).filter(Boolean)

  // -- NIF -----------------------------------------------------------------
  // Procura todos os blocos de 9 digitos (com/sem PT, com/sem separadores)
  const nifPattern = /\b(?:PT\s*)?(\d{3}[\s.\-]?\d{3}[\s.\-]?\d{3})\b/gi
  const nifsTodos = []
  let m
  while ((m = nifPattern.exec(text)) !== null) {
    const valor = m[1].replace(/[\s.\-]/g, '')
    if (valor.length === 9) {
      const inicio = Math.max(0, m.index - 60)
      const contexto = text.slice(inicio, m.index).toLowerCase()
      const score = /(nif|contribuint|n[uú]mero\s*fiscal|n[oº]\s*fiscal|tax\s*id|vat)/i.test(contexto) ? 10 : 1
      nifsTodos.push({ valor, score, pos: m.index })
    }
  }
  nifsTodos.sort((a, b) => b.score - a.score || a.pos - b.pos)
  const nif = nifsTodos[0]?.valor || null

  // -- Valores monetarios ---------------------------------------------------
  // Estrategia: para cada keyword identificada na linha, extrair o PRIMEIRO
  // valor monetario logo a seguir (a direita) da keyword. Robusto contra
  // linhas multi-coluna tipo "Base 1.095,48 IVA 251,96" (apanha o valor
  // correcto para cada keyword separadamente).
  let totalPdf = null
  let semIvaPdf = null
  let ivaPdf = null

  // Cada elemento e [regex_keyword, callback(valor)]
  // Ordem importa: keywords mais especificas/longas primeiro (evita "iva"
  // apanhar dentro de "total c/iva" antes do "total c/iva" ser testado)
  const KW_TOTAL = /\b(total\s*(?:c|com)\s*\/?\s*iva|total\s*a\s*pagar|total\s*geral|total\s*da\s*fatura|total\s*do\s*documento|valor\s*total|total)\b\s*[:.€]?/gi
  const KW_SEM_IVA = /\b(base\s*tribut[aá]vel|total\s*s\s*\/?\s*iva|sub.?total\s*il[ií]quido|valor\s*sem\s*iva|total\s*l[ií]quido|subtotal|sub\s*total|base)\b\s*[:.€]?/gi
  const KW_IVA = /\b(montante\s*iva|valor\s*iva|total\s*iva|total\s*do\s*iva|imposto|iva)\b\s*\(?\s*(?:\d+[,.]?\d*\s*%\s*\)?)?\s*[:.€]?/gi

  // Para cada linha:
  //   1) Procura keywords MAIS ESPECIFICAS primeiro (sem_iva, iva)
  //   2) Marca as posicoes que ja foram "ocupadas" por essas keywords
  //   3) Procura TOTAL ignorando as zonas ocupadas (evita apanhar "total"
  //      sozinho dentro de "total s/iva" ou "total iva")
  //   4) Para cada keyword, extrai o PRIMEIRO valor monetario logo a seguir
  for (const linha of linhas) {
    KW_SEM_IVA.lastIndex = 0
    const mSem = KW_SEM_IVA.exec(linha)
    KW_IVA.lastIndex = 0
    const mIva = KW_IVA.exec(linha)

    const reservadas = []
    if (mSem) reservadas.push([mSem.index, mSem.index + mSem[0].length])
    if (mIva) reservadas.push([mIva.index, mIva.index + mIva[0].length])

    KW_TOTAL.lastIndex = 0
    let mTot = null
    let cand
    while ((cand = KW_TOTAL.exec(linha)) !== null) {
      const dentro = reservadas.some(([s, e]) => cand.index >= s && cand.index < e)
      // "total c/iva" é match preferido, nunca filtrar
      const totalCIva = /total\s*c\s*\/?\s*iva/i.test(cand[0])
      if (totalCIva || !dentro) { mTot = cand; break }
    }

    if (mSem) {
      const v = extrairPrimeiroValorMonetarioApos(linha, mSem.index + mSem[0].length)
      if (v) semIvaPdf = v.valor
    }
    if (mIva) {
      const v = extrairPrimeiroValorMonetarioApos(linha, mIva.index + mIva[0].length)
      if (v) ivaPdf = v.valor
    }
    if (mTot) {
      const v = extrairPrimeiroValorMonetarioApos(linha, mTot.index + mTot[0].length)
      if (v) totalPdf = v.valor
    }
  }

  // Fallback: se nao apanhou total, tomar o MAIOR valor monetario detectado
  if (totalPdf == null) {
    const todos = linhas.flatMap(l => {
      const v = extrairUltimoValorMonetario(l)
      return v ? [v.valor] : []
    })
    if (todos.length > 0) totalPdf = Math.max(...todos)
  }

  // Coherence: derivar IVA / total a partir dos outros se possivel
  if (totalPdf == null && semIvaPdf != null && ivaPdf != null) {
    totalPdf = Number((semIvaPdf + ivaPdf).toFixed(2))
  }
  if (ivaPdf == null && totalPdf != null && semIvaPdf != null) {
    const impl = Number((totalPdf - semIvaPdf).toFixed(2))
    if (impl > 0) ivaPdf = impl
  }
  if (semIvaPdf == null && totalPdf != null && ivaPdf != null) {
    const impl = Number((totalPdf - ivaPdf).toFixed(2))
    if (impl > 0) semIvaPdf = impl
  }

  // -- Nome do cliente -----------------------------------------------------
  // Estrategia robusta:
  //   1) Se a encomenda passou nifEsperado, localizar ESSE NIF no PDF e usar
  //      a posicao para procurar o nome a volta (mais fiavel que "ultimo NIF").
  //   2) Caso contrario: assume "ultimo NIF = cliente" (heuristica original).
  // Filtra linhas tecnicas (e-mail com/sem hifen, data, codigos postais PT,
  // ATCUD, IBAN, etc.) que confundem o utilizador.
  const ehLinhaTecnica = (linha) => {
    const l = (linha || '').trim()
    if (l.length < 3) return true
    if (/^(nif|contribuint|morada|c[oó]digo\s*postal|telefone|tel\.|fax|e-?mail|data|fatura|invoice|n[ºoº]\s|atcud|iban|swift|natureza|processado|este\s+documento|p[áa]gina\s+\d)/i.test(l)) return true
    // Codigo postal PT: 1234-567
    if (/^\d{4}\s*-\s*\d{3}\b/.test(l)) return true
    // So digitos / so pontuacao
    if (/^[\d\s\-,.]+$/.test(l)) return true
    return false
  }

  const nomeCandidates = []
  const nifsPorOrdem = [...nifsTodos].sort((a, b) => a.pos - b.pos)
  let linhaIdxNif = -1
  let nifClienteEncontradoNoTexto = false

  // 1ª via: procurar directamente nas linhas pelo NIF esperado (em varios formatos)
  // Mais robusto que mapear text.indexOf para linhas[] (que sao trim+filter)
  if (nifEsperadoNorm) {
    const variantes = [
      nifEsperadoNorm,
      nifEsperadoNorm.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3'),
      nifEsperadoNorm.replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3'),
      nifEsperadoNorm.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3'),
    ]
    for (let i = 0; i < linhas.length; i++) {
      if (variantes.some(v => linhas[i].includes(v))) {
        linhaIdxNif = i
        nifClienteEncontradoNoTexto = true
        break
      }
    }
  }

  // Fallback: usar a ULTIMA linha que contem um NIF detectado pelo regex
  if (linhaIdxNif < 0 && nifsPorOrdem.length > 0) {
    const nifFallback = nifsPorOrdem.length >= 2
      ? nifsPorOrdem[nifsPorOrdem.length - 1].valor
      : nifsPorOrdem[0].valor
    for (let i = linhas.length - 1; i >= 0; i--) {
      if (linhas[i].includes(nifFallback)) { linhaIdxNif = i; break }
    }
  }

  if (linhaIdxNif >= 0) {
    // Olhar 4 linhas antes e 4 depois -- exclui linhas tecnicas
    for (let off = -4; off <= 4; off++) {
      const li = linhaIdxNif + off
      if (li >= 0 && li < linhas.length && li !== linhaIdxNif) {
        const cand = linhas[li]
        if (!ehLinhaTecnica(cand)) nomeCandidates.push(cand)
      }
    }
  }
  // Linhas com keywords explicitas
  linhas.forEach((l, idx) => {
    if (/^(cliente|exmo|faturar\s*a|destinat[aá]rio|enviar\s*a|nome)\s*[:\.]?/i.test(l)) {
      const semKey = l.replace(/^(cliente|exmo|faturar\s*a|destinat[aá]rio|enviar\s*a|nome)\s*[:\.]?/i, '').trim()
      if (semKey.length >= 3) nomeCandidates.push(semKey)
      if (linhas[idx + 1] && !ehLinhaTecnica(linhas[idx + 1])) nomeCandidates.push(linhas[idx + 1])
    }
  })

  // -- Nº fatura ------------------------------------------------------------
  // Padroes comuns: "Fatura nº FT 2024/946", "Fatura n.º FA 2026/68", "FR 2026/12", etc.
  // Captura prefixo letras (2-3) + ano(4) + / + numero (1-6)
  let numero_fatura = null
  const faturaPatterns = [
    /Fatura\s*n[º°o]?[:\.\s]*([A-Z]{1,4}\s*\d{2,4}\s*\/\s*\d{1,6})/i,
    /(?:^|\s)([A-Z]{2,4}\s*\d{4}\s*\/\s*\d{1,6})(?:\s*[-—–]\s*(?:original|duplicado|cópia|copia))?/i,
    /N[º°o]?\s*Documento[:\.\s]*([A-Z]{1,4}\s*\d{2,4}\s*\/\s*\d{1,6})/i,
  ]
  for (const re of faturaPatterns) {
    const m = text.match(re)
    if (m && m[1]) {
      numero_fatura = m[1].replace(/\s+/g, ' ').trim()
      break
    }
  }

  // -- Data emissao ---------------------------------------------------------
  // Patterns: "Data: 2026-06-14" (Sage), "Data: 14/06/2026", "Data Emissao: 14-06-2026"
  let data_emissao = null
  const dataPatterns = [
    /Data\s*(?:de\s*)?emiss[ãa]o[:\.\s]*(\d{4}-\d{2}-\d{2})/i,
    /Data\s*(?:de\s*)?emiss[ãa]o[:\.\s]*(\d{2}\/\d{2}\/\d{4})/i,
    /(?<!vencimento[:\.\s]*\d{0,10}\s*)Data[:\.\s]+(\d{4}-\d{2}-\d{2})\b/i,
    /(?<!vencimento[:\.\s]*\d{0,10}\s*)Data[:\.\s]+(\d{2}\/\d{2}\/\d{4})\b/i,
  ]
  // Estrategia simples: primeira "Data:" que NAO seja "Data de vencimento"
  for (const linha of linhas) {
    if (/vencimento/i.test(linha)) {
      // Pode haver "Data: X    Data de vencimento: Y" na mesma linha
      // Apanhar so antes de "vencimento"
      const antes = linha.split(/data\s*de\s*vencimento/i)[0]
      const m = antes.match(/Data[:\.\s]+(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})/i)
      if (m && m[1]) { data_emissao = m[1]; break }
    } else if (/^data[:\.\s]/i.test(linha) || /(?<!\w)data[:\.\s]+(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})/i.test(linha)) {
      const m = linha.match(/Data[:\.\s]+(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})/i)
      if (m && m[1]) { data_emissao = m[1]; break }
    }
  }
  // Normalizar para YYYY-MM-DD
  if (data_emissao && /^\d{2}\/\d{2}\/\d{4}$/.test(data_emissao)) {
    const [d, mo, y] = data_emissao.split('/')
    data_emissao = `${y}-${mo}-${d}`
  }

  // Se o NIF esperado foi encontrado no texto (mesmo que o regex de deteccao
  // de NIFs nao o tenha apanhado), usar esse como NIF detectado. Evita falso
  // positivo "NIF nao bate" quando o regex pegou no NIF do emitente.
  const nifFinal = nifClienteEncontradoNoTexto ? nifEsperadoNorm : nif

  return {
    nif: nifFinal,
    nome_candidates: nomeCandidates.filter(c => c && c.length >= 3),
    full_text: text,
    total: totalPdf,
    sem_iva: semIvaPdf,
    iva: ivaPdf,
    numero_fatura,
    data_emissao,
    debug: {
      nifs_encontrados: nifsTodos.map(x => x.valor),
      nif_cliente_encontrado_no_texto: nifClienteEncontradoNoTexto,
      total_chars: text.length,
      n_linhas: linhas.length,
    },
  }
}

/** Extrai apenas Nº fatura e data — utilitário para auto-preencher form
 *  antes de submeter para validacao completa. Reusa extractTextFromPdf + extrairCampos.
 *  @returns {Promise<{numero_fatura: string|null, data_emissao: string|null}>}
 */
export async function extrairCabecalhoFaturaPdf(file) {
  try {
    const text = await extractTextFromPdf(file)
    const out = extrairCampos(text)
    return { numero_fatura: out.numero_fatura, data_emissao: out.data_emissao }
  } catch {
    return { numero_fatura: null, data_emissao: null }
  }
}

/** Match aproximado de nome -- pelo menos 60% das palavras (>=3 letras) bate */
function nomeBateAprox(esperado, candidates) {
  if (!esperado || !candidates?.length) return false
  const tokensEsperado = norm(esperado).split(/\s+/).filter(t => t.length >= 3)
  if (tokensEsperado.length === 0) return false
  for (const cand of candidates) {
    const tokensCand = norm(cand).split(/\s+/).filter(Boolean)
    const matches = tokensEsperado.filter(t => tokensCand.some(c => c.includes(t) || t.includes(c)))
    const ratio = matches.length / tokensEsperado.length
    if (ratio >= 0.6) return true
  }
  return false
}

/** Match no texto completo do PDF (raw text). Mais robusto que candidatos:
 *  procura tokens "longos" (>=5 letras) do nome esperado no PDF inteiro.
 *  Requer 60% dos tokens longos como minimo. Casos de nomes muito curtos
 *  (ex: "Ana Lopes" - 2 tokens >=5) requer pelo menos 2 matches. */
function nomeBatePorTextoCompleto(esperado, textoCompleto) {
  if (!esperado || !textoCompleto) return false
  const tokensLongos = norm(esperado).split(/\s+/).filter(t => t.length >= 5)
  if (tokensLongos.length === 0) return false
  const textoNorm = norm(textoCompleto)
  const matches = tokensLongos.filter(t => textoNorm.includes(t))
  const minRequer = Math.max(2, Math.ceil(tokensLongos.length * 0.6))
  return matches.length >= Math.min(minRequer, tokensLongos.length)
}

/**
 * @param {object} encomenda -- objecto modalPag (cliente_nome, entrega_nif, total_sem_iva, desconto_valor, total_final)
 * @param {boolean} interno -- true se cliente_interno (sem IVA)
 */
function calcularEsperado(encomenda, interno, totalEsperado = null) {
  const sumSemIva = Number(encomenda.total_sem_iva) || 0
  const desc = Number(encomenda.desconto_valor) || 0
  const totalEnc = Number(encomenda.total_final) || 0
  // Uma encomenda pode ser facturada em DUAS facturas: uma contra o sinal e
  // outra contra o restante. Nesse caso o total desta factura nao e o total da
  // encomenda, e comparar com ele acusava discrepancia em todas.
  //
  // A base e o IVA seguem a mesma proporcao do total, para o triplo
  // base+IVA=total continuar a fechar em cada uma das facturas.
  const total = totalEsperado != null ? Number(totalEsperado) : totalEnc
  const proporcao = totalEnc > 0 ? total / totalEnc : 1
  const base = Math.max(0, sumSemIva - desc) * proporcao
  const iva = interno ? 0 : Math.max(0, total - base)
  return { sem_iva: Number(base.toFixed(2)), iva: Number(iva.toFixed(2)), total: Number(total.toFixed(2)) }
}

/**
 * Verifica o PDF da factura contra a encomenda.
 * @param {File|Blob} file
 * @param {object} encomenda -- modalPag
 * @param {boolean} interno
 * @param {object} opts -- { ignorarNomeNif: boolean }
 *   - ignorarNomeNif: para clientes cujas facturas saem em nome doutra entidade
 *     (ex: Diogo facturado a plataforma intermediaria). Verifica so valores.
 * @returns {Promise<{
 *   ok: boolean,
 *   ilegivel: boolean,
 *   problemas: Array<{campo:string, esperado:string, encontrado:string}>,
 *   extraido: object,
 *   esperado: object,
 * }>}
 */
export async function verifyFaturaClientePdf(file, encomenda, interno = false, opts = {}) {
  const ignorarNomeNif = !!opts.ignorarNomeNif
  // Total que ESTA factura deve ter. Omitido = o total da encomenda.
  const totalEsperado = opts.totalEsperado != null ? Number(opts.totalEsperado) : null
  let text = ''
  try {
    text = await extractTextFromPdf(file)
  } catch (e) {
    return {
      ok: false, ilegivel: true, problemas: [{ campo: 'PDF', esperado: '', encontrado: 'Erro ao ler: ' + (e?.message || String(e)) }],
      extraido: {}, esperado: calcularEsperado(encomenda, interno, totalEsperado),
    }
  }
  if (!text || text.trim().length < 50) {
    return {
      ok: false, ilegivel: true,
      problemas: [{ campo: 'PDF', esperado: '', encontrado: 'PDF sem texto extraivel (provavelmente imagem digitalizada)' }],
      extraido: { raw_preview: text.slice(0, 200) }, esperado: calcularEsperado(encomenda, interno, totalEsperado),
    }
  }

  // NIF esperado: opts.nifEsperado tem prioridade (caller pode injectar do perfil
  // quando encomenda.entrega_nif esta vazio). Fallback: encomenda.entrega_nif.
  const esperadoNif = normNif(opts.nifEsperado || encomenda.entrega_nif || '')
  const esperadoNome = encomenda.cliente_nome || encomenda.entrega_nome || ''
  // Passa NIF esperado ao extrairCampos para localizar correctamente a area do cliente
  const extraido = extrairCampos(text, { nifEsperado: esperadoNif })
  const esperado = calcularEsperado(encomenda, interno, totalEsperado)

  const problemas = []

  // NIF e Nome -- so verifica se ignorarNomeNif=false
  if (!ignorarNomeNif) {
    if (esperadoNif) {
      if (!extraido.nif) {
        problemas.push({ campo: 'NIF', esperado: esperadoNif, encontrado: '-- (nao detectado no PDF)' })
      } else if (extraido.nif !== esperadoNif) {
        problemas.push({ campo: 'NIF', esperado: esperadoNif, encontrado: extraido.nif })
      }
    }
    if (esperadoNome) {
      // 1ª tentativa: match via candidatos (zona perto do NIF)
      // 2ª tentativa: match via texto completo do PDF (mais robusto, evita
      //   falsos negativos quando candidatos foram apanhados na zona errada)
      const matchCandidatos = nomeBateAprox(esperadoNome, extraido.nome_candidates)
      const matchTextoCompleto = !matchCandidatos && nomeBatePorTextoCompleto(esperadoNome, extraido.full_text || '')
      if (!matchCandidatos && !matchTextoCompleto) {
        const sample = extraido.nome_candidates.slice(0, 3).join(' | ') || '--'
        problemas.push({ campo: 'Nome', esperado: esperadoNome, encontrado: sample })
      }
    }
  }

  // Total c/IVA -- obrigatorio SEMPRE (mesmo com ignorarNomeNif)
  if (extraido.total == null) {
    problemas.push({ campo: 'Total c/IVA', esperado: esperado.total.toFixed(2) + ' EUR', encontrado: '-- (nao detectado)' })
  } else if (Math.abs(extraido.total - esperado.total) > TOL_VALOR) {
    problemas.push({ campo: 'Total c/IVA', esperado: esperado.total.toFixed(2) + ' EUR', encontrado: extraido.total.toFixed(2) + ' EUR' })
  }

  // Total s/IVA -- se detectado, tem de bater
  if (extraido.sem_iva != null && Math.abs(extraido.sem_iva - esperado.sem_iva) > TOL_VALOR) {
    problemas.push({ campo: 'Total s/IVA', esperado: esperado.sem_iva.toFixed(2) + ' EUR', encontrado: extraido.sem_iva.toFixed(2) + ' EUR' })
  }

  // IVA -- so verifica para nao-internos quando detectado
  if (!interno && extraido.iva != null && Math.abs(extraido.iva - esperado.iva) > TOL_VALOR) {
    problemas.push({ campo: 'IVA', esperado: esperado.iva.toFixed(2) + ' EUR', encontrado: extraido.iva.toFixed(2) + ' EUR' })
  }

  return {
    ok: problemas.length === 0,
    ilegivel: false,
    problemas,
    extraido: {
      nif: extraido.nif,
      nome_candidates: extraido.nome_candidates,
      total: extraido.total,
      sem_iva: extraido.sem_iva,
      iva: extraido.iva,
    },
    esperado: {
      nif: esperadoNif,
      nome: esperadoNome,
      ...esperado,
    },
  }
}
